"use client";

import { ShoppingBagIcon } from "lucide-react";

import { PrefetchLink } from "@/components/prefetch-link.client";
import { useCartCount } from "@/lib/cart/use-cart";
import { usePluralTranslator } from "@/lib/translation-context";

import styles from "./hf-cart.module.css";

/**
 * The Cart element's live surface (feature: header-footer-builder).
 *
 * The one place this feature adds a client component, and it adds no new
 * dependency to a traveler's page: `useCartCount` and `PrefetchLink` are both
 * already in the storefront bundle because `nav.tsx` ships them on every route.
 * A cart count cannot be server-rendered — it changes without a navigation — so
 * a slot filled with something static would show a stale number, which is worse
 * than the few hundred bytes this costs.
 *
 * Colour comes from the row it lands in (`--hf-fg`), never from a storefront
 * token, so the cart reads correctly on a dark band. The badge keeps the
 * agency's own primary colour, which is the accent the standard header uses
 * for it too — a count is a notification, not body text.
 */
export function HfCart(): React.ReactElement {
  const tn = usePluralTranslator();
  const itemCount = useCartCount();

  return (
    <PrefetchLink
      href="/cart"
      aria-label={tn("Cart ({count} trip)", "Cart ({count} trips)", itemCount, {
        count: itemCount,
      })}
      className={styles.cart}
    >
      <ShoppingBagIcon className={styles.icon} strokeWidth={1.5} />
      {itemCount > 0 ? <span className={styles.badge}>{itemCount}</span> : null}
    </PrefetchLink>
  );
}
